'use client';

import React, { ReactElement } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Typography,
} from '@mui/material';
import { Close, ExpandMore, ExpandLess } from '@mui/icons-material';
import { Controller, UseFormHandleSubmit, Control, FieldErrors } from 'react-hook-form';
import { Chip } from '@mui/material';
import CustomRadioButton from '../form/CustomRadioButton';
import CustomSelectField from '../form/CustomSelectField';
import FileUploader from '../form/FileUploader';
import { assigneeLabels } from '../../../constants/assignee';
import { type AssigneeOption } from '../../../utils/assigneeDisplay';
import {
  fileProcessingType,
  geminiModel,
  geminiSamplingParam,
  ocrEngineType,
  ocrEngineOption,
  ocrEngineChoice,
  getOcrEngineOptionId,
  structuredEngineType,
  getGeminiModelPriceLabel,
  GPT4O_PRICE_LABEL,
  geminiThinkingSlider,
  getGeminiThinkingType,
  sliderValueToThinking,
  thinkingToSliderValue,
  OCR_UPLOAD_MAX_TOTAL_PAGES,
  OCR_UPLOAD_MAX_PAGES_PER_FILE,
  OCR_UPLOAD_FILE_MAX_BYTES,
  GEMINI_MODEL_DEFAULT,
} from '../../../constants/enum';
import { isPdfFile } from '../../../utils/pdfToImage';
import {
  getDisplayUploadLimit,
  calculateOcrLimitInfo,
  getOcrLimitOverMessage,
  getOcrCreditUsageMessage,
} from '../../../utils/userUsageLimit';
import { getOcrCreditRateWithModel } from '../../../constants/plans';
import { gray40, gray60, gray80, gray100, white, orange50 } from '../../../constants/colors';
import { boldMd, formLabelWrapper, regularLg, regularXl } from '../../../constants/location';
import {
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import type { CompanyContextType } from '../../../contexts/auth';
import type { UserSetType } from '../../../types/user';
import NoAccountIcon from '../../assets/Account circle.svg';

// アップロード制限は constants/enum.ts に集約（メール取り込み経路とも共通の値）
const MAX_TOTAL_PAGES = OCR_UPLOAD_MAX_TOTAL_PAGES;
const MAX_PAGES_PER_FILE = OCR_UPLOAD_MAX_PAGES_PER_FILE;
// 注意喚起テキスト表示用の1ファイル最大サイズ（MB）
const FILE_MAX_MB = OCR_UPLOAD_FILE_MAX_BYTES / 1024 / 1024;

type UploadFormValues = { images: File[] };

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UploadFormValues) => Promise<void>;
  control: Control<UploadFormValues>;
  handleSubmit: UseFormHandleSubmit<UploadFormValues>;
  errors: FieldErrors<UploadFormValues>;
  watchedImages: File[];
  isSubmitted: boolean;
  processingTypeValue: number;
  onProcessingTypeChange: (newValue: number, currentFiles: File[]) => Promise<void>;
  onFilesSelected: (files: FileList | null, onChange: (value: File[]) => void) => Promise<void>;
  onFileDeleted: (index: number) => void;
  pdfPageCountCache: Map<string, number>;
  selectedGeminiModel: number;
  onGeminiModelChange: (newModel: number) => void;
  thinkingBudgetValue: number | null;
  onThinkingBudgetChange: (value: number) => void;
  thinkingLevelValue: string | null;
  onThinkingLevelChange: (value: string | null) => void;
  // OCRエンジン切替（#2960）。パターンAI-OCRでのみ使用。フリーOCR等では未指定で Vision 固定。
  ocrEngine?: number;
  onOcrEngineChange?: (value: number) => void;
  // Geminiサンプリングパラメータ（#2960 運営スタッフ限定・1回目OCR抽出用）
  geminiTemperature?: number;
  onGeminiTemperatureChange?: (value: number) => void;
  geminiTopP?: number;
  onGeminiTopPChange?: (value: number) => void;
  // 行の矩形座標を Gemini に渡すか（#2960 方式B・運営スタッフ限定）
  includeCoordinates?: boolean;
  onIncludeCoordinatesChange?: (value: boolean) => void;
  // 2回目構造化エンジン（#2960 structuredEngineType・運営スタッフ限定）
  structuredEngine?: number;
  onStructuredEngineChange?: (value: number) => void;
  /**
   * 検証用UI（OCRエンジン・思考・座標・sampling・構造化）を表示できるか（#2960 / #3069）。
   * 呼び出し元が useDetailMode().isDetailView を渡す。詳細モードOFF時は一般ユーザーと同じ画面にする。
   */
  canUseVerificationSettings?: boolean;
  isProAllowedPattern: boolean;
  /**
   * DI系が設定された読取パターンか（#3264）。
   * true なら一般ユーザーにも OCR エンジンの選択を出す。
   * 1つのパターンに手書きと表が混ざる場合に、帳票ごとに選び分けられるようにするため
   */
  isOcrEngineSelectablePattern?: boolean;
  totalPageCount: number;
  isCalculating: boolean;
  baseRemainingCount: number | null;
  user: UserSetType | null;
  company: CompanyContextType | null;
  fileCountOverText?: string;
  proxyDialogSx?: object | null;
  /**
   * 個人別 月間OCR上限の超過メッセージを返す関数（#3133）。
   * 未指定時は個人別上限のチェックを行わない。
   */
  getUserLimitMessage?: (addPages: number) => string | null;
  /** 担当者選択を表示するか（#2642）。未指定時は非表示 */
  showAssigneeSelect?: boolean;
  /** 担当者候補リスト（#2642） */
  assigneeOptions?: AssigneeOption[];
  /** 選択中の担当者UID（#2642） */
  selectedAssigneeUid?: string;
  /** 担当者選択変更時のコールバック（#2642） */
  onAssigneeChange?: (uid: string) => void;
};

const UploadModal = ({
  open,
  onClose,
  onSubmit,
  control,
  handleSubmit,
  errors,
  watchedImages,
  isSubmitted,
  processingTypeValue,
  onProcessingTypeChange,
  onFilesSelected,
  onFileDeleted,
  pdfPageCountCache,
  selectedGeminiModel,
  onGeminiModelChange,
  thinkingBudgetValue,
  onThinkingBudgetChange,
  thinkingLevelValue,
  onThinkingLevelChange,
  ocrEngine = ocrEngineType.visionAi,
  onOcrEngineChange,
  geminiTemperature = geminiSamplingParam.temperatureDefault,
  onGeminiTemperatureChange,
  geminiTopP = geminiSamplingParam.topPDefault,
  onGeminiTopPChange,
  includeCoordinates = false,
  onIncludeCoordinatesChange,
  structuredEngine = structuredEngineType.gemini,
  onStructuredEngineChange,
  canUseVerificationSettings = false,
  isProAllowedPattern,
  isOcrEngineSelectablePattern = false,
  totalPageCount,
  isCalculating,
  baseRemainingCount,
  user,
  company,
  fileCountOverText,
  proxyDialogSx,
  getUserLimitMessage,
  showAssigneeSelect = false,
  assigneeOptions = [],
  selectedAssigneeUid = '',
  onAssigneeChange,
}: Props): ReactElement => {
  // 上限超過フラグの計算（ボタン非活性用）
  // 代理モード中も月間枠・OCRクレジットの上限を意図的にそのまま判定する（develop仕様）。
  const isOverLimit = ((): boolean => {
    if (totalPageCount > MAX_TOTAL_PAGES) return true;
    // 個人別 月間OCR上限（#3133）
    if (getUserLimitMessage?.(totalPageCount)) return true;
    if (!user || baseRemainingCount === null || !company) return false;
    const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
    return totalPageCount > limitInfo.totalRemainingCount;
  })();

  // ファイルアップロードのバリデーションメッセージを取得
  // 代理モード中も月間枠・OCRクレジット消費メッセージは意図的にそのまま表示する（develop仕様）。
  const getFileValidationMessage = (): string | undefined => {
    if (!user || isSubmitted) return undefined;
    if (errors.images?.message) return errors.images.message;
    if (totalPageCount > MAX_TOTAL_PAGES) {
      return `合計ページ数が上限を超えています。（${totalPageCount}ページ / 上限${MAX_TOTAL_PAGES}ページ）`;
    }
    // 個人別 月間OCR上限（#3133）。会社枠より先に判定する。
    const userLimitMessage = getUserLimitMessage?.(totalPageCount);
    if (userLimitMessage) return userLimitMessage;
    if (user && baseRemainingCount !== null && company) {
      const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
      return getOcrLimitOverMessage(totalPageCount, limitInfo) ?? undefined;
    }
    return undefined;
  };

  // クレジットから消費される旨の情報メッセージ（警告ではなく情報トーンで表示する）
  const getCreditInfoMessage = (): string | undefined => {
    if (!user || isSubmitted) return undefined;
    if (errors.images?.message) return undefined;
    if (totalPageCount > MAX_TOTAL_PAGES) return undefined;
    // 個人別上限で止まっている場合はクレジット消費の案内を出さない（#3133）
    if (getUserLimitMessage?.(totalPageCount)) return undefined;
    if (baseRemainingCount === null || !company) return undefined;
    const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
    return getOcrCreditUsageMessage(totalPageCount, limitInfo) ?? undefined;
  };

  // Geminiパラメータ（temp/topP/思考量）の折りたたみ開閉状態（#2960）。
  // パラメータは常に有効（送信）で、UI表示のみ折りたたむ。
  const [isGeminiParamsOpen, setIsGeminiParamsOpen] = React.useState(false);

  // Geminiパラメータのスライダー色をブランドオレンジに合わせる（#2960 運営限定UI）
  const sliderStyle = {
    color: orange50,
    '& .MuiSlider-thumb': { backgroundColor: orange50 },
    '& .MuiSlider-track': { backgroundColor: orange50, borderColor: orange50 },
  };

  const styles = {
    dialog: {
      '& .MuiPaper-root': {
        border: '1px solid' + gray40,
        boxSizing: 'border-box',
        margin: '16px',
        padding: 0,
        width: '480px',
        maxHeight: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
      },
    },
    dialogCloseButton: {
      height: '32px',
      width: '32px',
      '@media screen and (max-width:960px)': {
        height: '24px',
        width: '24px',
      },
    },
    dialogHeader: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      padding: '16px 32px 12px',
      flexShrink: 0,
      '@media screen and (max-width:960px)': {
        padding: '16px 16px 8px',
      },
    },
    dialogTitle: {
      ...regularXl,
      '@media screen and (max-width:960px)': {
        ...regularLg,
      },
    },
    dialogContent: {
      padding: '0 32px 16px',
      flex: 1,
      overflowY: 'auto' as const,
      '@media screen and (max-width:960px)': {
        padding: '0 16px 12px',
      },
    },
    dialogButtonWrapper: {
      display: 'flex',
      gap: '16px',
      padding: '12px 32px 16px',
      borderTop: `1px solid ${gray40}`,
      backgroundColor: white,
      flexShrink: 0,
      '@media screen and (max-width:960px)': {
        gap: '8px',
        padding: '8px 16px 16px',
      },
    },
    uploadCancelButton: {
      ...tertiaryButtonBackgroundColor,
      height: '44px',
      width: 'calc(50% - 8px)',
    },
    uploadCancelButtonText: {
      ...boldMd,
      color: gray80,
    },
    uploadSubmitButton: {
      height: '44px',
      width: 'calc(50% - 8px)',
      ...primaryButtonBackgroundColor,
    },
    uploadSubmitButtonText: {
      ...boldMd,
      color: white,
    },
    uploadFileChip: {
      margin: '0 8px 8px 0',
    },
  };

  return (
    <Dialog open={open} sx={[styles.dialog, proxyDialogSx ?? {}]}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>ファイルをアップロード</Typography>
        <IconButton sx={styles.dialogCloseButton} onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={styles.dialogContent}>
        {/* 担当者選択（#2642）ダイアログ最上部 / ラベルは CustomRadioButton と統一スタイル */}
        {showAssigneeSelect && (
          <Box sx={{ marginBottom: '16px' }}>
            <Typography align="left" sx={formLabelWrapper} component="div">
              <Typography sx={{ color: gray80, fontSize: '14px' }} component="span">
                {assigneeLabels.label}
              </Typography>
            </Typography>
            <Select
              displayEmpty
              value={selectedAssigneeUid}
              onChange={(e): void => onAssigneeChange?.(e.target.value)}
              size="small"
              fullWidth
              // 他の Select と統一: focus 時もデフォルトの青枠でなく gray100
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: gray60,
                  borderWidth: '1px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: gray100,
                  borderWidth: '1px',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: gray100,
                  borderWidth: '1px',
                },
              }}
              renderValue={(value: string): ReactElement | undefined => {
                if (!value) {
                  return <span style={{ color: gray60 }}>{assigneeLabels.selectPlaceholder}</span>;
                }
                const selected = assigneeOptions.find(o => o.uid === value);
                if (!selected) return <span>{value}</span>;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar
                      src={selected.iconUrl || NoAccountIcon}
                      sx={{ width: 24, height: 24, fontSize: '12px' }}
                    />
                    <span>{selected.name}</span>
                  </Box>
                );
              }}
            >
              <MenuItem value="">{assigneeLabels.selectPlaceholder}</MenuItem>
              {assigneeOptions.map(opt => (
                <MenuItem key={opt.uid} value={opt.uid}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar
                      src={opt.iconUrl || NoAccountIcon}
                      sx={{ width: 24, height: 24, fontSize: '12px' }}
                    />
                    <span>{opt.name}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* ファイル処理タイプセレクター */}
        <Box sx={{ marginBottom: '12px' }}>
          <CustomRadioButton
            value={String(processingTypeValue)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              const newValue = Number(event.target.value);
              onProcessingTypeChange(newValue, watchedImages);
            }}
            labelText="ファイル処理方式"
            required={false}
            firstOption="1ページごとに出力"
            secondOption="複数ページをまとめて出力"
            firstOptionDescription="ページごとに分割してOCR処理"
            secondOptionDescription="複数ページを統合してOCR処理"
            firstValue={fileProcessingType.pagePerResult}
            secondValue={fileProcessingType.singleResult}
          />
        </Box>

        {/* ① OCRエンジン（運営スタッフのみ・#2960。エンジン×座標付与を1つに統合） */}
        {canUseVerificationSettings && (
          <Box sx={{ marginBottom: '16px', maxWidth: '400px' }}>
            <Typography sx={{ color: gray100, fontSize: '14px', marginBottom: '8px' }}>
              OCRエンジン
            </Typography>
            <CustomSelectField
              value={getOcrEngineOptionId(ocrEngine, includeCoordinates)}
              onChange={(event): void => {
                const selected = ocrEngineOption.find(o => o.id === Number(event.target.value));
                if (selected) {
                  // 選択値を（エンジン, 座標付与）に展開して親に通知する。
                  onOcrEngineChange?.(selected.ocrEngine);
                  onIncludeCoordinatesChange?.(selected.includeCoordinates);
                }
              }}
              labelText=""
              requiredText=""
              list={ocrEngineOption.map(o => ({ option: o.label, value: o.id }))}
            />
          </Box>
        )}

        {/* ①' 読み取り方式（#3264 DI系が設定されたパターン向け）。
            帳票の特徴で選ぶ3択だけを出す。エンジン名と座標付与は出さない。
            課金は変わらないため費用も出さない。

            詳細モードONの運営には上の①（エンジン×座標の8通り）があるのでここは出さない。
            詳細モードOFFの運営・代理ログイン・一般ユーザーが対象。
            Gemini モデルの isProAllowedPattern と同じ考え方 */}
        {!canUseVerificationSettings && isOcrEngineSelectablePattern && (
          <Box sx={{ marginBottom: '16px', maxWidth: '400px' }}>
            {/* ラベルは CustomSelectField の labelText に任せる。
                自前の Typography で書くと「ファイル処理方式」等と文字色が揃わない */}
            <CustomSelectField
              value={ocrEngine}
              onChange={(event): void => {
                onOcrEngineChange?.(Number(event.target.value));
              }}
              labelText="読み取り方式"
              requiredText=""
              list={ocrEngineChoice.map(c => ({ option: c.label, value: c.value }))}
            />
          </Box>
        )}

        {/* ② 値取得エンジン（Geminiモデル + パラメータ）。検証設定利用可 または Proパターン。
            代理ログイン中は詳細OFFでも Proパターンならモデル選択だけは残す（#2960） */}
        {(canUseVerificationSettings || isProAllowedPattern) && (
          <Box sx={{ marginBottom: '16px', maxWidth: '400px' }}>
            {/* 運営時のみ「値取得エンジン」グループヘッダーを出す（一般Proユーザーは従来ラベル） */}
            {canUseVerificationSettings && (
              <Typography sx={{ color: gray100, fontSize: '14px', marginBottom: '8px' }}>
                値取得エンジン
              </Typography>
            )}
            <CustomSelectField
              value={selectedGeminiModel}
              onChange={(event): void => {
                const newModel = Number(event.target.value);
                onGeminiModelChange(newModel);
              }}
              labelText={canUseVerificationSettings ? '' : 'Geminiモデル'}
              requiredText="必須"
              list={
                canUseVerificationSettings
                  ? [
                      // 一般運用でも使う正式モデル（この下に区切り線）
                      {
                        option: 'Gemini 3.5 Flash-Lite（デフォルト・低コスト）',
                        description: getGeminiModelPriceLabel(GEMINI_MODEL_DEFAULT),
                        value: GEMINI_MODEL_DEFAULT,
                      },
                      {
                        option: 'Gemini 2.5 Pro（高精度）',
                        description: getGeminiModelPriceLabel(geminiModel.pro_2_5),
                        value: geminiModel.pro_2_5,
                        divider: true,
                      },
                      // ここから下は検証用モデル
                      {
                        option: 'Gemini 2.5 Flash（廃止予定）',
                        description: getGeminiModelPriceLabel(geminiModel.flash_2_5),
                        value: geminiModel.flash_2_5,
                      },
                      {
                        option: 'Gemini 3 Flash（高速・高性能）',
                        description: getGeminiModelPriceLabel(geminiModel.flash_3),
                        value: geminiModel.flash_3,
                      },
                      {
                        option: 'Gemini 3.1 Flash-Lite（低コスト）',
                        description: getGeminiModelPriceLabel(geminiModel.flash_lite_3_1),
                        value: geminiModel.flash_lite_3_1,
                      },
                      {
                        option: 'Gemini 3.1 Pro（最高精度）',
                        description: getGeminiModelPriceLabel(geminiModel.pro_3),
                        value: geminiModel.pro_3,
                      },
                      {
                        option: 'Gemini 3.5 Flash（高速・高性能）',
                        description: getGeminiModelPriceLabel(geminiModel.flash_3_5),
                        value: geminiModel.flash_3_5,
                      },
                    ]
                  : [
                      // 一般ユーザー向け。既定モデル（#3375）と Pro の2択。
                      {
                        option: `Gemini 3.5 Flash-Lite（高速）- ${getOcrCreditRateWithModel(company?.plan ?? 0, GEMINI_MODEL_DEFAULT)}円/枚`,
                        value: GEMINI_MODEL_DEFAULT,
                      },
                      {
                        option: `Gemini 2.5 Pro（高精度）- ${getOcrCreditRateWithModel(company?.plan ?? 0, geminiModel.pro_2_5)}円/枚`,
                        value: geminiModel.pro_2_5,
                      },
                    ]
              }
            />
          </Box>
        )}

        {/* Geminiパラメータ（運営スタッフのみ・#2960。1回目OCR抽出用。temp/topP + 思考量）
            パラメータは常に有効。ヘッダークリックでスライダー部を折りたたむ。 */}
        {canUseVerificationSettings && (
          <Box sx={{ marginBottom: '16px', maxWidth: '400px' }}>
            {/* 折りたたみヘッダー（クリックで開閉） */}
            <Box
              onClick={(): void => setIsGeminiParamsOpen(prev => !prev)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Typography sx={{ color: gray80, fontSize: '14px' }}>
                Geminiパラメータ（1回目OCR抽出）
              </Typography>
              {isGeminiParamsOpen ? (
                <ExpandLess sx={{ color: gray80 }} />
              ) : (
                <ExpandMore sx={{ color: gray80 }} />
              )}
            </Box>
            {isGeminiParamsOpen && (
              <Box sx={{ paddingX: '8px', paddingTop: '4px' }}>
                {/* temperature */}
                <Typography sx={{ color: gray80, fontSize: '13px', marginTop: '8px' }}>
                  temperature: {geminiTemperature}
                </Typography>
                <Slider
                  value={geminiTemperature}
                  min={geminiSamplingParam.temperatureMin}
                  max={geminiSamplingParam.temperatureMax}
                  step={geminiSamplingParam.temperatureStep}
                  valueLabelDisplay="auto"
                  sx={sliderStyle}
                  onChange={(_event, value): void => onGeminiTemperatureChange?.(Number(value))}
                />
                {/* topP */}
                <Typography sx={{ color: gray80, fontSize: '13px', marginTop: '8px' }}>
                  topP: {geminiTopP}
                </Typography>
                <Slider
                  value={geminiTopP}
                  min={geminiSamplingParam.topPMin}
                  max={geminiSamplingParam.topPMax}
                  step={geminiSamplingParam.topPStep}
                  valueLabelDisplay="auto"
                  sx={sliderStyle}
                  onChange={(_event, value): void => onGeminiTopPChange?.(Number(value))}
                />
                {/* 思考量（0=無効〜4=高の5段。モデルに応じ thinkingBudget/thinkingLevel に変換）。
                    思考非対応モデル（Flash-Lite系）は disabled。未選択はデフォルト0。 */}
                {((): ReactElement => {
                  const isThinkingDisabled = getGeminiThinkingType(selectedGeminiModel) === 'none';
                  const thinkingSliderValue = thinkingToSliderValue(
                    selectedGeminiModel,
                    thinkingBudgetValue ?? null,
                    thinkingLevelValue ?? null,
                  );
                  return (
                    <>
                      <Typography sx={{ color: gray80, fontSize: '13px', marginTop: '8px' }}>
                        思考量: {geminiThinkingSlider.labels[thinkingSliderValue] ?? '無効'}
                        {isThinkingDisabled && '（このモデルは非対応）'}
                      </Typography>
                      <Slider
                        value={thinkingSliderValue}
                        min={geminiThinkingSlider.min}
                        max={geminiThinkingSlider.max}
                        step={1}
                        marks
                        disabled={isThinkingDisabled}
                        valueLabelDisplay="off"
                        sx={sliderStyle}
                        onChange={(_event, value): void => {
                          const { thinkingBudget, thinkingLevel } = sliderValueToThinking(
                            selectedGeminiModel,
                            Number(value),
                          );
                          onThinkingBudgetChange(thinkingBudget ?? 0);
                          onThinkingLevelChange(thinkingLevel);
                        }}
                      />
                    </>
                  );
                })()}
              </Box>
            )}
          </Box>
        )}

        {/* ③ 構造化エンジン（運営スタッフのみ・#2960。デフォルトは Gemini） */}
        {canUseVerificationSettings && (
          <Box sx={{ marginBottom: '16px', maxWidth: '400px' }}>
            <Typography sx={{ color: gray100, fontSize: '14px', marginBottom: '8px' }}>
              構造化エンジン
            </Typography>
            <CustomSelectField
              value={structuredEngine}
              onChange={(event): void => {
                onStructuredEngineChange?.(Number(event.target.value));
              }}
              labelText=""
              requiredText=""
              list={[
                // 既定（structuredEngineType.gemini）は #3375 で 3.1 Flash-Lite に変更。
                // geminiFlashLite31 と同じモデルになるため、選択肢はこの1行に統合する。
                {
                  option: 'Gemini 3.1 Flash-Lite（デフォルト）',
                  description: getGeminiModelPriceLabel(geminiModel.flash_lite_3_1),
                  value: structuredEngineType.gemini,
                },
                {
                  option: 'Gemini 3.5 Flash-Lite',
                  description: getGeminiModelPriceLabel(geminiModel.flash_lite_3_5),
                  value: structuredEngineType.geminiFlashLite35,
                },
                {
                  option: 'ChatGPT（GPT-4o）',
                  description: GPT4O_PRICE_LABEL,
                  value: structuredEngineType.chatgpt,
                },
              ]}
            />
          </Box>
        )}

        {/* 選択済みファイルチップ */}
        {watchedImages.length > 0 && (
          <Box sx={{ marginBottom: '12px' }}>
            {watchedImages.flatMap((image: File, index: number) => {
              const nameWithoutExt = image.name.replace(/\.[^/.]+$/, '');
              const ext = image.name.match(/\.[^/.]+$/)?.[0] ?? '';
              const pageCount =
                processingTypeValue === fileProcessingType.pagePerResult && isPdfFile(image)
                  ? (pdfPageCountCache.get(image.name) ?? 1)
                  : 1;
              return Array.from({ length: pageCount }, (_, pageIndex) => {
                const label =
                  pageCount > 1 ? `${nameWithoutExt}_${pageIndex + 1}${ext}` : image.name;
                return (
                  <Chip
                    key={`${index}-${pageIndex}`}
                    label={label}
                    onDelete={pageIndex === 0 ? (): void => onFileDeleted(index) : undefined}
                    sx={styles.uploadFileChip}
                    variant="outlined"
                  />
                );
              });
            })}
          </Box>
        )}

        {/* ファイルアップローダー */}
        <Controller
          name="images"
          control={control}
          render={({ field }): ReactElement => (
            <FileUploader
              onFilesSelected={(files: FileList | null): void => {
                void onFilesSelected(files, field.onChange);
              }}
              helperText={getFileValidationMessage()}
              infoText={getCreditInfoMessage()}
              fileCountOverText={fileCountOverText}
            />
          )}
        />

        {/* 注意書き */}
        <Typography sx={{ marginTop: '12px', fontSize: '13px', color: gray80 }}>
          ・一度にアップロードできるファイルは最大{getDisplayUploadLimit(company)}
          ファイルまで
          <br />
          ・一度にアップロードできるページ数は合計{MAX_TOTAL_PAGES}ページまで
          <br />
          ・各ファイルのサイズは{FILE_MAX_MB}MBまで
          <br />
          ・複数ページをまとめて出力する場合、PDFは1ファイルあたり
          {MAX_PAGES_PER_FILE}ページまで
        </Typography>
      </Box>

      <Box sx={styles.dialogButtonWrapper}>
        <Button sx={styles.uploadCancelButton} onClick={onClose}>
          <Typography sx={styles.uploadCancelButtonText}>キャンセル</Typography>
        </Button>
        <Button
          sx={styles.uploadSubmitButton}
          onClick={handleSubmit(onSubmit)}
          disabled={watchedImages.length === 0 || !!errors.images || isOverLimit || isCalculating}
        >
          <Typography sx={styles.uploadSubmitButtonText}>アップロード</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default UploadModal;
